import { useState } from "react";
import AddBoxIcon from '@mui/icons-material/AddBox';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ListPaginator from 'applications/signal/components/ListPaginator';
import UserTeamUpsertModal from "applications/signal/components/UserTeamUpsertModal";
import UserTeamMemberManageModal from "../UserTeamMemberManageModal";
import { useDeleteSignalUserTeam } from "applications/signal/hooks/gqlHooks";
import { AlertSnackbarProps } from "features/components/alertSnackbar";
import { SignalUserTeam } from "gql/graphql";
import { setGlobalMessage } from "hooks/useGlobalMessage";

type Props = {
  rows: SignalUserTeam[];
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  totalCount: number;
  setSnackBarMessage: (message: AlertSnackbarProps | null) => void;
};

export default function UserTeamList({
  rows, page, rowsPerPage, setPage, setRowsPerPage, totalCount, setSnackBarMessage
}: Props) {
  const [selectedTeam, setSelectedTeam] = useState<SignalUserTeam | null>(null);
  const [isUpsertModalOpen, setIsUpsertModalOpen] = useState(false);
  const [isMemberManageModalOpen, setIsMemberManageModalOpen] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState<boolean>(false);
  const { deleteSignalUserTeam } = useDeleteSignalUserTeam();

  const handleNewTeamClick = () => {
    setSelectedTeam(null);
    setIsUpsertModalOpen(true);
  };

  const handleMemberManageClick = (e: React.MouseEvent<HTMLButtonElement>, team: SignalUserTeam) => {
    e.stopPropagation();
    setSelectedTeam(team);
    setIsMemberManageModalOpen(true);
  };

  const handleEditTeamClick = async (e: React.MouseEvent<HTMLButtonElement>, team: SignalUserTeam) => {
    e.stopPropagation();
    setSelectedTeam(team);
    setIsUpsertModalOpen(true);
  };

  const handleDeleteTeamClick = async (e: React.MouseEvent<HTMLButtonElement>, team: SignalUserTeam) => {
    e.stopPropagation();
    if (window.confirm('本当に削除しますか？')) {
      if (!team.id) return;
      setDeleteProcessing(true);
      const { data: result, error } = await deleteSignalUserTeam({
        id: team.id
      });
      if (error) {
        setGlobalMessage({
          message: "チームの削除に失敗しました。",
          severity: "error",
        });
      } else {
        setSnackBarMessage({
          message: "チームを削除しました。",
          severity: "success",
        });
      }
      setDeleteProcessing(false);
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <h2 style={{ marginLeft: "20px" }}>チーム管理</h2>
      <Box sx={{ display: 'flex', justifyContent: 'end', lineHeight: '21px', padding: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddBoxIcon />}
          onClick={handleNewTeamClick}
        >
          追加
        </Button>
      </Box>
      <TableContainer>
        <Table stickyHeader area-label="sticky table">
          <TableHead>
            <TableRow key="header">
              <TableCell align={"center"}>
                チーム名
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={`${row.id}`}>
                    <TableCell key={`name-${index}`} align={"center"}>
                      {`${row.name}`}
                    </TableCell>
                    <TableCell key={`memberManage-${index}`} align={"right"} sx={{ width: '200px' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PeopleIcon />}
                        onClick={(e) => handleMemberManageClick(e, row)}
                      >
                        メンバー管理
                      </Button>
                    </TableCell>
                    <TableCell key={`actions-${index}`} align={"right"} sx={{ width: '220px' }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ mr: 2 }}
                        startIcon={<EditIcon />}
                        onClick={(e) => handleEditTeamClick(e, row)}
                      >
                        編集
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<DisabledByDefaultIcon />}
                        onClick={(e) => handleDeleteTeamClick(e, row)}
                        disabled={deleteProcessing}
                      >
                        削除
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="flex-end">
        <ListPaginator
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Box>
      <UserTeamUpsertModal
        registered={selectedTeam}
        open={isUpsertModalOpen}
        setOpen={setIsUpsertModalOpen}
        setSnackBarMessage={setSnackBarMessage}
      />
      <UserTeamMemberManageModal
        userTeamId={selectedTeam ? Number(selectedTeam.id!) : 0}
        open={isMemberManageModalOpen}
        setOpen={setIsMemberManageModalOpen}
        setSnackBarMessage={setSnackBarMessage}
      />
    </Paper>
  )
}
